import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import AutoClosedPunches from './AutoClosedPunches';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogFormGrid, DialogFormSection } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { attendanceRulesAPI, employeeAPI, reportsAPI } from '../services/api';
import Swal from 'sweetalert2';
import { Check, X, Clock, Plus, Edit, Trash2, AlertTriangle, BarChart3, CalendarClock, ShieldCheck, FileText, Users, UserCheck, UserX, ClipboardList, Radio } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { TableExportActions } from './ui/table-export-actions';
import { tooltipContentStyle, tooltipCursor, axisProps, gridProps } from '../lib/chartTheme';
import { EmptyState } from './common/States';
import { StatCard } from './common/StatCard';

const AttendanceManagement = ({ overviewStats = null, trendData = [], hiddenTabs = [] }) => {
  const navigate = useNavigate();
  const getAttendanceStatusVariant = (status) => {
    switch ((status || '').toUpperCase()) {
      case 'PRESENT':
        return 'success';
      case 'ABSENT':
        return 'danger';
      case 'LATE':
        return 'warning';
      case 'HALF_DAY':
      case 'HALFDAY':
        return 'halfday';
      case 'ON_LEAVE':
      case 'LEAVE':
        return 'info';
      default:
        return 'outline';
    }
  };

  const [summaries, setSummaries] = useState([]);
  const [musterRegister, setMusterRegister] = useState([]);
  const [musterSummary, setMusterSummary] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [rules, setRules] = useState([]);
  
  const [filters, setFilters] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    employeeId: "all",
    approvalStatus: "all"
  });

  const [showShiftDialog, setShowShiftDialog] = useState(false);
  const [currentShift, setCurrentShift] = useState(null); // For edit
  const [shiftForm, setShiftForm] = useState({
      name: '', start_time: '09:00:00', end_time: '18:00:00', 
      grace_period_minutes: 15, early_exit_tolerance_minutes: 15,
      is_default: false
  });

  const [currentRule, setCurrentRule] = useState(null);
  const [showRuleDialog, setShowRuleDialog] = useState(false);
  const [ruleForm, setRuleForm] = useState({
      name: '', rule_type: 'late_entry', grace_minutes: 15, 
      threshold_count: 3, penalty_type: 'leave_deduction', penalty_value: 0.5
  });

  const isTabVisible = (tabId) => !hiddenTabs.includes(tabId);

  const attendanceSummaryExportRows = useMemo(
    () =>
      summaries.map((summary) => ({
        ...summary,
        work_hours: `${Math.floor((summary.total_minutes || 0) / 60)}h ${(summary.total_minutes || 0) % 60}m`,
      })),
    [summaries]
  );

  const attendanceSummaryExportColumns = useMemo(
    () => [
      { label: 'Date', accessor: (row) => row.date },
      { label: 'Employee ID', accessor: (row) => row.employee_id },
      { label: 'Status', accessor: (row) => row.status },
      { label: 'Work Hours', accessor: (row) => row.work_hours },
      { label: 'Late Minutes', accessor: (row) => row.late_minutes },
      { label: 'Early Minutes', accessor: (row) => row.early_minutes },
      { label: 'Overtime Minutes', accessor: (row) => row.overtime_minutes },
      { label: 'Approval Status', accessor: (row) => row.approval_status },
    ],
    []
  );

  const shiftsExportColumns = useMemo(
    () => [
      { label: 'Name', accessor: (row) => row.name },
      { label: 'Start Time', accessor: (row) => row.start_time },
      { label: 'End Time', accessor: (row) => row.end_time },
      { label: 'Grace Period Minutes', accessor: (row) => row.grace_period_minutes },
      { label: 'Early Exit Tolerance Minutes', accessor: (row) => row.early_exit_tolerance_minutes },
      { label: 'Default', accessor: (row) => (row.is_default ? 'Yes' : 'No') },
    ],
    []
  );

  const musterExportColumns = useMemo(() => {
    const firstRow = musterRegister[0];
    if (!firstRow) return [];
    return Object.keys(firstRow).map((key) => ({
      label: key.startsWith('day_') ? `Day ${key.slice(4)}` : key.replace(/_/g, ' '),
      accessor: (row) => row[key],
    }));
  }, [musterRegister]);

  useEffect(() => {
    fetchEmployees();
    fetchShifts();
    fetchRules();
  }, []);

  useEffect(() => {
    fetchSummaries();
    fetchMusterRegister();
    // The fetch function closes over the current filter state by design.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchEmployees = async () => {
    try {
      const res = await employeeAPI.list();
      setEmployees(res.data);
    } catch (error) {
      console.error("Failed to load employees");
    }
  };

  const fetchShifts = async () => {
    try {
      const res = await attendanceRulesAPI.getShifts();
      setShifts(res.data);
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load shifts' });
    }
  };

  const fetchRules = async () => {
    try {
      const res = await attendanceRulesAPI.getRules();
      setRules(res.data);
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load rules' });
    }
  };

  const fetchSummaries = async () => {
    try {
      const empId = filters.employeeId === "all" ? null : filters.employeeId;
      const status = filters.approvalStatus === "all" ? null : filters.approvalStatus;
      const res = await attendanceRulesAPI.getSummary(empId, filters.month, filters.year, status);
      setSummaries(res.data);
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load attendance summaries' });
    }
  };

  const fetchMusterRegister = async () => {
    try {
      const empId = filters.employeeId === "all" ? null : filters.employeeId;
      const status = filters.approvalStatus === "all" ? null : filters.approvalStatus;
      const res = await reportsAPI.musterRegister({
        month: filters.month,
        year: filters.year,
        employee_id: empId,
        approval_status: status,
      });
      setMusterRegister(Array.isArray(res.data?.data) ? res.data.data : []);
      setMusterSummary(res.data?.summary || null);
    } catch (error) {
      setMusterRegister([]);
      setMusterSummary(null);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load muster register' });
    }
  };

  const handleApprove = async (id) => {
    try {
      await attendanceRulesAPI.updateSummary(id, { approval_status: "approved" });
      Swal.fire({ icon: 'success', title: 'Success', text: 'Attendance approved', timer: 1500, showConfirmButton: false });
      fetchSummaries();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to approve' });
    }
  };

  const handleReject = async (id) => {
    try {
      await attendanceRulesAPI.updateSummary(id, { approval_status: "rejected" });
      Swal.fire({ icon: 'success', title: 'Success', text: 'Attendance rejected', timer: 1500, showConfirmButton: false });
      fetchSummaries();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to reject' });
    }
  };

  const handleCreateShift = async () => {
    try {
      if (currentShift) {
        await attendanceRulesAPI.updateShift(currentShift.id, shiftForm);
        Swal.fire({ icon: 'success', title: 'Success', text: 'Shift updated', timer: 1500, showConfirmButton: false });
      } else {
        await attendanceRulesAPI.createShift(shiftForm);
        Swal.fire({ icon: 'success', title: 'Success', text: 'Shift created', timer: 1500, showConfirmButton: false });
      }
      setShowShiftDialog(false);
      setCurrentShift(null);
      fetchShifts();
      setShiftForm({
        name: '', start_time: '09:00:00', end_time: '18:00:00', 
        grace_period_minutes: 15, early_exit_tolerance_minutes: 15,
        is_default: false
      });
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to save shift' });
    }
  };

  const handleDeleteShift = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: 'hsl(var(--destructive))',
      cancelButtonColor: 'hsl(var(--primary))',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
          await attendanceRulesAPI.deleteShift(id);
          Swal.fire('Deleted!', 'Shift has been deleted.', 'success');
          fetchShifts();
      } catch (error) {
          Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to delete shift' });
      }
    }
  };

  const handleEditShift = (shift) => {
      setCurrentShift(shift);
      setShiftForm({
          name: shift.name,
          start_time: shift.start_time,
          end_time: shift.end_time,
          grace_period_minutes: shift.grace_period_minutes,
          early_exit_tolerance_minutes: shift.early_exit_tolerance_minutes,
          is_default: shift.is_default
      });
      setShowShiftDialog(true);
  };

  const handleCreateRule = async () => {
    try {
        if (currentRule) {
            await attendanceRulesAPI.updateRule(currentRule.id, ruleForm);
            Swal.fire({ icon: 'success', title: 'Success', text: 'Rule updated', timer: 1500, showConfirmButton: false });
        } else {
            await attendanceRulesAPI.createRule(ruleForm);
            Swal.fire({ icon: 'success', title: 'Success', text: 'Rule created', timer: 1500, showConfirmButton: false });
        }
        setShowRuleDialog(false);
        setCurrentRule(null);
        fetchRules();
        setRuleForm({
            name: '', rule_type: 'late_entry', grace_minutes: 15, 
            threshold_count: 3, penalty_type: 'leave_deduction', penalty_value: 0.5
        });
    } catch (error) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to save rule' });
    }
  };

  const handleDeleteRule = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: 'hsl(var(--primary))',
      cancelButtonColor: 'hsl(var(--destructive))',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
          await attendanceRulesAPI.deleteRule(id);
          Swal.fire('Deleted!', 'Rule has been deleted.', 'success');
          fetchRules();
      } catch (error) {
          Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to delete rule' });
      }
    }
  };

  const handleEditRule = (rule) => {
      setCurrentRule(rule);
      setRuleForm({
          name: rule.name,
          rule_type: rule.rule_type,
          grace_minutes: rule.grace_minutes,
          threshold_count: rule.threshold_count,
          penalty_type: rule.penalty_type,
          penalty_value: rule.penalty_value
      });
      setShowRuleDialog(true);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <Tabs defaultValue="summary" className="w-full">
        <TabsList className="sticky top-2 z-20 border border-border bg-background">
            {isTabVisible("summary") && <TabsTrigger value="summary" className="gap-1.5">
              <BarChart3 className="h-3.5 w-3.5" />
              Attendance Summary
            </TabsTrigger>}
            {isTabVisible("muster") && <TabsTrigger value="muster" className="gap-1.5">
              <FileText className="h-3.5 w-3.5" />
              Muster Register
            </TabsTrigger>}
            {isTabVisible("shifts") && <TabsTrigger value="shifts" className="gap-1.5">
              <CalendarClock className="h-3.5 w-3.5" />
              Shift Configuration
            </TabsTrigger>}
            {isTabVisible("rules") && <TabsTrigger value="rules" className="gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5" />
              Attendance Rules
            </TabsTrigger>}
            {isTabVisible("auto-closed") && <TabsTrigger value="auto-closed" className="gap-1.5">
              <Radio className="h-3.5 w-3.5" />
              Auto-Closed
            </TabsTrigger>}
        </TabsList>

        <TabsContent value="summary" className="animate-slide-up">
            {overviewStats && (
                <div className="mb-6 space-y-6">
                    <div className="grid gap-4 md:grid-cols-3">
                        <StatCard
                            label="Present Today"
                            value={overviewStats.todayAttendance}
                            icon={UserCheck}
                            tone="success"
                            hint="Employees marked present today"
                        />
                        <StatCard
                            label="Attendance Rate"
                            value={Number(overviewStats.attendanceRate) || 0}
                            suffix="%"
                            decimals={1}
                            tone="primary"
                            ring={Number(overviewStats.attendanceRate) || 0}
                            hint="Based on total active employees"
                        />
                        <StatCard
                            label="Employees Counted"
                            value={overviewStats.totalEmployees}
                            icon={Users}
                            tone="info"
                            hint="Total employees in the dashboard base"
                        />
                    </div>

                    <Card className="hover-lift border-border shadow-sm transition-all duration-200">
                        <CardHeader>
                            <CardTitle>Attendance Trend (Last 7 Days)</CardTitle>
                        </CardHeader>
                        <CardContent className="h-[280px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={trendData}>
                                    <CartesianGrid {...gridProps} />
                                    <XAxis dataKey="name" {...axisProps} />
                                    <YAxis allowDecimals={false} {...axisProps} />
                                    <Tooltip cursor={tooltipCursor} contentStyle={tooltipContentStyle} />
                                    <Bar dataKey="count" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} name="Present Employees" />
                                </BarChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>
                </div>
            )}

            <Card className="hover-lift border-border shadow-sm transition-all duration-200">
                <CardHeader>
                <CardTitle>Daily Attendance Records</CardTitle>
                </CardHeader>
                <CardContent>
                <div className="flex gap-4 mb-6 flex-wrap">
                    <div className="space-y-1">
                    <label className="text-xs font-medium">Month</label>
                    <Select 
                        value={filters.month.toString()} 
                        onValueChange={(val) => setFilters({...filters, month: parseInt(val)})}
                    >
                        <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Month" />
                        </SelectTrigger>
                        <SelectContent>
                        {Array.from({length: 12}, (_, i) => (
                            <SelectItem key={i+1} value={(i+1).toString()}>
                            {new Date(0, i).toLocaleString('default', { month: 'long' })}
                            </SelectItem>
                        ))}
                        </SelectContent>
                    </Select>
                    </div>

                    <div className="space-y-1">
                    <label className="text-xs font-medium">Year</label>
                    <Select 
                        value={filters.year.toString()} 
                        onValueChange={(val) => setFilters({...filters, year: parseInt(val)})}
                    >
                        <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Year" />
                        </SelectTrigger>
                        <SelectContent>
                        <SelectItem value="2024">2024</SelectItem>
                        <SelectItem value="2025">2025</SelectItem>
                        <SelectItem value="2026">2026</SelectItem>
                        </SelectContent>
                    </Select>
                    </div>

                    <div className="space-y-1">
                    <label className="text-xs font-medium">Employee</label>
                    <Select 
                        value={filters.employeeId} 
                        onValueChange={(val) => setFilters({...filters, employeeId: val})}
                    >
                        <SelectTrigger className="w-[200px]">
                        <SelectValue placeholder="All Employees" />
                        </SelectTrigger>
                        <SelectContent>
                        <SelectItem value="all">All Employees</SelectItem>
                        {employees.map(emp => (
                            <SelectItem key={emp.employee_id} value={emp.employee_id}>
                            {emp.first_name} {emp.last_name} ({emp.employee_id})
                            </SelectItem>
                        ))}
                        </SelectContent>
                    </Select>
                    </div>

                    <div className="space-y-1">
                    <label className="text-xs font-medium">Approval Status</label>
                    <Select 
                        value={filters.approvalStatus} 
                        onValueChange={(val) => setFilters({...filters, approvalStatus: val})}
                    >
                        <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                        </SelectContent>
                    </Select>
                    </div>
                </div>

                <div className="mb-3 flex justify-end">
                  <TableExportActions
                    rows={attendanceSummaryExportRows}
                    columns={attendanceSummaryExportColumns}
                    fileName={`attendance_summary_${filters.year}_${String(filters.month).padStart(2, '0')}`}
                    title="Attendance Summary"
                  />
                </div>

                <div className="rounded-md border">
                    <Table>
                    <TableHeader>
                        <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Employee</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Work Hours</TableHead>
                        <TableHead>Late/Early</TableHead>
                        <TableHead>Overtime</TableHead>
                        <TableHead>Approval</TableHead>
                        <TableHead>Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {summaries.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={8} className="h-auto p-0">
                            <EmptyState
                                icon={CalendarClock}
                                title="No attendance records found"
                                description="Adjust the month, year, employee, or approval status filters to see attendance summaries."
                            />
                            </TableCell>
                        </TableRow>
                        ) : (
                        summaries.map((summary) => (
                            <TableRow key={summary.id}>
                            <TableCell>{summary.date}</TableCell>
                            <TableCell>{summary.employee_id}</TableCell>
                            <TableCell>
                                <Badge variant={getAttendanceStatusVariant(summary.status)}>
                                {summary.status}
                                </Badge>
                            </TableCell>
                            <TableCell>{Math.floor(summary.total_minutes / 60)}h {summary.total_minutes % 60}m</TableCell>
                            <TableCell>
                                {summary.late_minutes > 0 && <span className="text-danger block text-xs">Late: {summary.late_minutes}m</span>}
                                {summary.early_minutes > 0 && <span className="text-warning block text-xs">Early: {summary.early_minutes}m</span>}
                            </TableCell>
                            <TableCell>
                                {summary.overtime_minutes > 0 ? (
                                <span className="text-success font-bold">+{summary.overtime_minutes}m</span>
                                ) : '-'}
                            </TableCell>
                            <TableCell>
                                <Badge variant={
                                summary.approval_status === 'approved' ? 'success' : 
                                summary.approval_status === 'rejected' ? 'danger' : 'warning'
                                }>
                                {summary.approval_status}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                {summary.approval_status === 'pending' && (
                                <div className="flex gap-2">
                                    <Button size="icon" variant="outline" className="h-8 w-8 border-success/20 text-success hover:text-success hover:bg-success/10 hover:border-success/30 transition-colors" onClick={() => handleApprove(summary.id)}>
                                    <Check className="h-4 w-4" />
                                    </Button>
                                    <Button size="icon" variant="outline" className="h-8 w-8 border-destructive/20 text-destructive hover:text-destructive hover:bg-destructive/10 hover:border-destructive/30 transition-colors" onClick={() => handleReject(summary.id)}>
                                    <X className="h-4 w-4" />
                                    </Button>
                                </div>
                                )}
                            </TableCell>
                            </TableRow>
                        ))
                        )}
                    </TableBody>
                    </Table>
                </div>
                </CardContent>
            </Card>
        </TabsContent>

        <TabsContent value="muster" className="animate-slide-up">
          <Card className="hover-lift border-border shadow-sm transition-all duration-200">
            <CardHeader>
              <CardTitle>Muster Register</CardTitle>
            </CardHeader>
            <CardContent>
              {musterSummary && (
                <div className="mb-6 grid gap-4 md:grid-cols-4">
                  <StatCard label="Employees" value={musterSummary.total_employees || 0} icon={Users} tone="info" />
                  <StatCard label="Present Days" value={musterSummary.present_days || 0} icon={UserCheck} tone="success" />
                  <StatCard label="Absent Days" value={musterSummary.absent_days || 0} icon={UserX} tone="danger" />
                  <StatCard label="Late Marks" value={musterSummary.late_marks || 0} icon={Clock} tone="warning" />
                </div>
              )}

              <div className="flex gap-4 mb-6 flex-wrap">
                <div className="space-y-1">
                  <label className="text-xs font-medium">Month</label>
                  <Select
                    value={filters.month.toString()}
                    onValueChange={(val) => setFilters({ ...filters, month: parseInt(val, 10) })}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Month" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 12 }, (_, i) => (
                        <SelectItem key={i + 1} value={(i + 1).toString()}>
                          {new Date(0, i).toLocaleString('default', { month: 'long' })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-medium">Year</label>
                  <Select
                    value={filters.year.toString()}
                    onValueChange={(val) => setFilters({ ...filters, year: parseInt(val, 10) })}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Year" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2024">2024</SelectItem>
                      <SelectItem value="2025">2025</SelectItem>
                      <SelectItem value="2026">2026</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-medium">Employee</label>
                  <Select
                    value={filters.employeeId}
                    onValueChange={(val) => setFilters({ ...filters, employeeId: val })}
                  >
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="All Employees" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Employees</SelectItem>
                      {employees.map(emp => (
                        <SelectItem key={emp.employee_id} value={emp.employee_id}>
                          {emp.first_name} {emp.last_name} ({emp.employee_id})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-medium">Approval Status</label>
                  <Select
                    value={filters.approvalStatus}
                    onValueChange={(val) => setFilters({ ...filters, approvalStatus: val })}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mb-3 flex justify-end">
                <TableExportActions
                  rows={musterRegister}
                  columns={musterExportColumns}
                  fileName={`muster_register_${filters.year}_${String(filters.month).padStart(2, '0')}`}
                  title="Muster Register"
                />
              </div>

              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {musterRegister[0] ? Object.keys(musterRegister[0]).map((key) => (
                        <TableHead key={key} className={key.startsWith('day_') ? 'text-center min-w-[58px]' : 'min-w-[120px]'}>
                          {key.startsWith('day_') ? key.slice(4) : key.replace(/_/g, ' ')}
                        </TableHead>
                      )) : null}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {musterRegister.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={40} className="h-auto p-0">
                          <EmptyState
                            icon={ClipboardList}
                            title="No muster records found"
                            description="No muster register entries match the selected month, year, employee, or status."
                          />
                        </TableCell>
                      </TableRow>
                    ) : (
                      musterRegister.map((row, rowIndex) => (
                        <TableRow key={`${row.employee_id}-${rowIndex}`}>
                          {Object.entries(row).map(([key, value]) => (
                            <TableCell key={key} className={key.startsWith('day_') ? 'text-center font-medium' : ''}>
                              {value?.toString() || '-'}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="shifts" className="animate-slide-up">
            <Card className="hover-lift border-border shadow-sm transition-all duration-200">
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Shift Configuration</CardTitle>
                    <Dialog open={showShiftDialog} onOpenChange={setShowShiftDialog}>
                        <DialogContent size="md">
                            <DialogHeader>
                                <DialogTitle>{currentShift ? 'Edit Shift' : 'Create New Shift'}</DialogTitle>
                            </DialogHeader>
                            <DialogFormSection className="py-4">
                                <div className="space-y-2">
                                    <Label>Shift Name</Label>
                                    <Input value={shiftForm.name} onChange={(e) => setShiftForm({...shiftForm, name: e.target.value})} placeholder="e.g. General Shift" />
                                </div>
                                <DialogFormGrid>
                                    <div className="space-y-2">
                                        <Label>Start Time</Label>
                                        <Input type="time" step="1" value={shiftForm.start_time} onChange={(e) => setShiftForm({...shiftForm, start_time: e.target.value})} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>End Time</Label>
                                        <Input type="time" step="1" value={shiftForm.end_time} onChange={(e) => setShiftForm({...shiftForm, end_time: e.target.value})} />
                                    </div>
                                </DialogFormGrid>
                                <DialogFormGrid>
                                    <div className="space-y-2">
                                        <Label>Grace Period (mins)</Label>
                                        <Input type="number" value={shiftForm.grace_period_minutes} onChange={(e) => setShiftForm({...shiftForm, grace_period_minutes: parseInt(e.target.value)})} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Early Exit Tolerance (mins)</Label>
                                        <Input type="number" value={shiftForm.early_exit_tolerance_minutes} onChange={(e) => setShiftForm({...shiftForm, early_exit_tolerance_minutes: parseInt(e.target.value)})} />
                                    </div>
                                </DialogFormGrid>
                                <div className="flex items-center space-x-2">
                                    <Checkbox id="is_default" checked={shiftForm.is_default} onCheckedChange={(checked) => setShiftForm({...shiftForm, is_default: checked})} />
                                    <Label htmlFor="is_default">Set as Default Shift</Label>
                                </div>
                                <Button className="w-full" onClick={handleCreateShift}>Save Shift</Button>
                            </DialogFormSection>
                        </DialogContent>
                    </Dialog>
                    <Button onClick={() => navigate('/admin/rules-shifts/create')}>
                        <Plus className="mr-2 h-4 w-4" /> Add Shift
                    </Button>
                </CardHeader>
                <CardContent>
                    <div className="mb-3 flex justify-end">
                        <TableExportActions
                            rows={shifts}
                            columns={shiftsExportColumns}
                            fileName="attendance_shifts"
                            title="Shift Configuration"
                        />
                    </div>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Timings</TableHead>
                                <TableHead>Grace Period</TableHead>
                                <TableHead>Early Tolerance</TableHead>
                                <TableHead>Default</TableHead>
                                <TableHead>Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {shifts.map((shift) => (
                                <TableRow key={shift.id}>
                                    <TableCell className="font-medium">{shift.name}</TableCell>
                                    <TableCell>{shift.start_time} - {shift.end_time}</TableCell>
                                    <TableCell>{shift.grace_period_minutes}m</TableCell>
                                    <TableCell>{shift.early_exit_tolerance_minutes}m</TableCell>
                                    <TableCell>{shift.is_default && <Badge>Default</Badge>}</TableCell>
                                    <TableCell>
                                        <div className="flex gap-2">
                                            <Button size="icon" variant="ghost" className="text-primary hover:text-primary/90 hover:bg-primary/10" onClick={() => handleEditShift(shift)}>
                                                <Edit className="h-4 w-4" />
                                            </Button>
                                            <Button size="icon" variant="ghost" className="text-destructive hover:text-destructive/90 hover:bg-destructive/10" onClick={() => handleDeleteShift(shift.id)}>
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </TabsContent>

          <TabsContent value="rules" className="space-y-4 animate-slide-up">
            <Card className="hover-lift transition-all duration-200 border-border shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between bg-muted/30 border-b border-border pb-4">
                <CardTitle className="text-lg font-semibold text-foreground">Attendance Rules</CardTitle>
              <Dialog open={showRuleDialog} onOpenChange={setShowRuleDialog}>
                <DialogTrigger asChild>
                  <Button onClick={() => { setCurrentRule(null); setRuleForm({
                    name: '',
                    rule_type: 'late_entry',
                    grace_minutes: 15,
                    threshold_count: 3,
                    penalty_type: 'leave_deduction',
                    penalty_value: 0.5
                  }); }} className="hover-lift shadow-sm bg-primary hover:bg-primary/90 text-primary-foreground">
                    <Plus className="mr-2 h-4 w-4" /> Add Rule
                  </Button>
                </DialogTrigger>
                <DialogContent size="md">
                  <DialogHeader>
                    <DialogTitle>{currentRule ? 'Edit Rule' : 'Add Rule'}</DialogTitle>
                  </DialogHeader>
                  <DialogFormSection className="py-4">
                    <div className="space-y-2">
                      <Label>Rule Name</Label>
                      <Input 
                        value={ruleForm.name}
                        onChange={(e) => setRuleForm({...ruleForm, name: e.target.value})}
                        placeholder="e.g. Late Entry Policy"
                        className="focus-visible:ring-primary"
                      />
                    </div>
                    <DialogFormGrid>
                      <div className="space-y-2">
                        <Label>Rule Type</Label>
                        <Select 
                          value={ruleForm.rule_type} 
                          onValueChange={(val) => setRuleForm({...ruleForm, rule_type: val})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="late_entry">Late Entry</SelectItem>
                            <SelectItem value="early_exit">Early Exit</SelectItem>
                            <SelectItem value="missing_punch">Missing Punch</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Grace Minutes</Label>
                        <Input 
                          type="number"
                          value={ruleForm.grace_minutes}
                          onChange={(e) => setRuleForm({...ruleForm, grace_minutes: parseInt(e.target.value)})}
                        />
                      </div>
                    </DialogFormGrid>
                    <DialogFormGrid>
                      <div className="space-y-2">
                        <Label>Threshold Count</Label>
                        <Input 
                          type="number"
                          value={ruleForm.threshold_count}
                          onChange={(e) => setRuleForm({...ruleForm, threshold_count: parseInt(e.target.value)})}
                          placeholder="Occurrences per month"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Penalty Type</Label>
                        <Select 
                          value={ruleForm.penalty_type} 
                          onValueChange={(val) => setRuleForm({...ruleForm, penalty_type: val})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="leave_deduction">Leave Deduction</SelectItem>
                            <SelectItem value="salary_deduction">Salary Deduction</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </DialogFormGrid>
                    <div className="space-y-2">
                      <Label>Penalty Value (Days/Amount)</Label>
                      <Input 
                        type="number"
                        step="0.5"
                        value={ruleForm.penalty_value}
                        onChange={(e) => setRuleForm({...ruleForm, penalty_value: parseFloat(e.target.value)})}
                      />
                    </div>
                  </DialogFormSection>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowRuleDialog(false)}>Cancel</Button>
                    <Button onClick={handleCreateRule} className="bg-primary hover:bg-primary/90 text-primary-foreground">Save Rule</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              </CardHeader>
              <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {rules.map((rule, index) => (
                <Card key={rule.id} className="hover-lift transition-all duration-200 border-border shadow-sm hover:shadow-md bg-card" style={{ animationDelay: `${index * 0.05}s` }}>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium text-card-foreground">
                      {rule.name}
                    </CardTitle>
                    <AlertTriangle className="h-4 w-4 text-warning" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-foreground">{rule.rule_type.replace('_', ' ').toUpperCase()}</div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Grace: {rule.grace_minutes}m | Threshold: {rule.threshold_count}
                    </p>
                    <div className="mt-3 flex items-center justify-between">
                      <Badge variant="secondary" className="bg-secondary text-secondary-foreground">
                        {rule.penalty_type === 'leave_deduction' ? 'Leave Cut' : 'Salary Cut'}: {rule.penalty_value}
                      </Badge>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-primary hover:text-primary/90 hover:bg-primary/10" onClick={() => {
                          setCurrentRule(rule);
                          setRuleForm(rule);
                          setShowRuleDialog(true);
                        }}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive/90 hover:bg-destructive/10" onClick={() => handleDeleteRule(rule.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="auto-closed" className="space-y-4 animate-slide-up">
            <AutoClosedPunches />
          </TabsContent>
      </Tabs>
    </div>
  );
};

export default AttendanceManagement;
